/**
 * @category 0.Services
 * @subcategory auth
 * @module auth
 * @desc ### Manages the connection with aws-amplify to enable authentication
 */
import { ref } from 'vue';
import { Auth } from 'aws-amplify';
import { useRolesStore } from '@/stores/roles';
import router from '@/router';

const userIdToken = ref('');

const POLO_LABELS: Record<string, string> = {
  MI: 'Milano',
  RM: 'Roma',
  NA: 'Napoli',
  PA: 'Palermo',
  BO: 'Bologna',
  DG: 'Direzione Generale',
  VE: 'Venezia',
  SD: 'Service Desk'
};

type PoloValue = string | string[] | Record<string, unknown> | null | undefined;

const normalizePolo = (polo: PoloValue): string[] => {
  if (Array.isArray(polo)) {
    return polo.flatMap(item => normalizePolo(item as PoloValue));
  }

  if (polo && typeof polo === 'object') {
    return Object.entries(polo).flatMap(([key, value]) => {
      if (typeof value === 'string' && value.toUpperCase() === 'POLO') {
        return [key];
      }

      return [key, ...normalizePolo(value as PoloValue)];
    });
  }

  if (typeof polo === 'string') {
    const trimmedPolo = polo.trim();

    if (trimmedPolo.startsWith('[') && trimmedPolo.endsWith(']')) {
      try {
        const parsedPolo = JSON.parse(trimmedPolo.replace(/'/g, '"'));

        if (Array.isArray(parsedPolo)) {
          return normalizePolo(parsedPolo);
        }
      } catch (error) {
        console.warn('Unable to parse polo array string:', error);
      }
    }

    return polo
      .split(',')
      .map(item => item.trim().replace(/^\[?['"]?/, '').replace(/['"]?\]?$/, ''))
      .filter(Boolean);
  }

  return [];
};

const extractReadablePolo = (polo: PoloValue): string => {
  const values = normalizePolo(polo);

  if (values.some(item => item.toUpperCase().includes('ADMIN'))) {
    return 'Admin';
  }

  return values
    .map(item => POLO_LABELS[item.toUpperCase()] || item)
    .join(', ');
};

/**
 * @desc If present saves the userIdToken, otherwise performs federated signin to .env COGNITO_REDIRECT_SIGN_IN.
 *
 * **Invoked by:**
 * - ##### onBeforeMount() - App.vue
 */
const authenticateUser = async () => {
  try {
    const session = await Auth.currentSession();
    if (!session) {
      console.error('No session found');
      throw new Error('No session');
    }
    const idToken = session.getIdToken();
    if (!idToken) {
      console.error('No ID token in session');
      throw new Error('No ID token');
    }

    userIdToken.value = idToken.getJwtToken();
    const accessTokenPayload = session.getAccessToken()?.payload;
    const idTokenPayload = idToken.payload;
    const accessTokenPolo = accessTokenPayload?.['custom:polo'] as PoloValue;
    const idTokenPolo = idTokenPayload?.['custom:polo'] as PoloValue;
    const rawPolo = accessTokenPolo || idTokenPolo || null;
    const rolesStore = useRolesStore();
    rolesStore.setPolo(rawPolo);
    console.group('Cognito polo debug');
    console.log('accessToken custom:polo:', accessTokenPolo);
    console.log('idToken custom:polo:', idTokenPolo);
    console.log('rawPolo saved in store:', rolesStore.getPolo);
    console.log('readable polo:', extractReadablePolo(rawPolo));
    console.log('calculated role:', rolesStore.getUserRole);
    console.log('isAdmin:', rolesStore.isAdmin);
    console.groupEnd();
    console.log('Authentication successful');
    return userIdToken.value;
  } catch (error) {
    console.error('Authentication error:', error);
    await router.push('/login');
    throw error;
  }
};

const signOut = async () => {
  Object.keys(localStorage)
    .filter(key => key.startsWith('CognitoIdentityServiceProvider'))
    .forEach(key => localStorage.removeItem(key));
  await router.push('/login');
};

export {
  userIdToken,
  authenticateUser,
  extractReadablePolo,
  signOut
};
